import { Award, Shield, Star } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export interface CertificateTemplate {
  id: string;
  name: string;
  style: 'classic' | 'modern' | 'minimal' | 'corporate' | 'academic';
  primaryColor: string;
  accentColor: string;
  backgroundColor?: string;
  logoUrl?: string;
  logoPosition: 'top-left' | 'top-center' | 'top-right';
  signatureUrl?: string;
  borderStyle: 'ornate' | 'simple' | 'none' | 'double';
  fontFamily: 'serif' | 'sans' | 'elegant' | 'modern' | 'handwritten';
  certificateTitle?: string;
  companyName?: string;
  issuerName?: string;
  issuerTitle?: string;
  footerText?: string;
  accreditationText?: string;
  isCustom?: boolean;
  companyId?: string;
}

export interface CertificateData {
  recipientName: string;
  pathwayName: string;
  certificationName?: string;
  completionDate: Date;
  score?: number;
  companyName?: string;
  issuerName?: string;
}

interface CertificatePreviewProps {
  template: CertificateTemplate;
  data: CertificateData;
  className?: string;
  scale?: number;
}

const defaultTemplates: CertificateTemplate[] = [
  {
    id: 'classic',
    name: 'Classic',
    style: 'classic',
    primaryColor: '#1a5f4a',
    accentColor: '#d4af37',
    logoPosition: 'top-center',
    borderStyle: 'ornate',
    fontFamily: 'serif',
    certificateTitle: 'Certificate of Completion',
  },
  {
    id: 'modern',
    name: 'Modern',
    style: 'modern',
    primaryColor: '#024223',
    accentColor: '#10b981',
    logoPosition: 'top-left',
    borderStyle: 'simple',
    fontFamily: 'sans',
    certificateTitle: 'Certificate of Completion',
  },
  {
    id: 'minimal',
    name: 'Minimal',
    style: 'minimal',
    primaryColor: '#1f2937',
    accentColor: '#3b82f6',
    logoPosition: 'top-left',
    borderStyle: 'none',
    fontFamily: 'sans',
    certificateTitle: 'Certificate of Completion',
  },
  {
    id: 'corporate',
    name: 'Corporate',
    style: 'corporate',
    primaryColor: '#1e3a5f',
    accentColor: '#0ea5e9',
    logoPosition: 'top-right',
    borderStyle: 'double',
    fontFamily: 'sans',
    certificateTitle: 'Certificate of Completion',
  },
  {
    id: 'academic',
    name: 'Academic',
    style: 'academic',
    primaryColor: '#4c1d95',
    accentColor: '#fbbf24',
    logoPosition: 'top-center',
    borderStyle: 'ornate',
    fontFamily: 'elegant',
    certificateTitle: 'Certificate of Achievement',
  }
];

export { defaultTemplates };

export function CertificatePreview({ template, data, className, scale = 1 }: CertificatePreviewProps) {
  const fontClass = {
    serif: 'font-serif',
    sans: 'font-sans',
    elegant: 'font-serif italic',
    modern: 'font-sans',
    handwritten: 'font-serif',
  }[template.fontFamily] || 'font-sans';

  const getBorderStyle = () => {
    switch (template.borderStyle) {
      case 'ornate':
        return 'border-8 border-double';
      case 'double':
        return 'border-4 border-double';
      case 'simple':
        return 'border-2';
      default:
        return '';
    }
  };

  const getBackgroundPattern = () => {
    if (template.backgroundColor) {
      return '';
    }
    switch (template.style) {
      case 'classic':
      case 'academic':
        return 'bg-gradient-to-br from-amber-50/50 via-white to-amber-50/30';
      case 'modern':
        return 'bg-gradient-to-br from-white via-emerald-50/20 to-white';
      case 'corporate':
        return 'bg-gradient-to-br from-slate-50 via-white to-blue-50/30';
      default:
        return 'bg-white';
    }
  };

  const displayCompanyName = template.companyName || data.companyName || '';
  const displayCertTitle = template.certificateTitle || 'Certificate of Completion';
  const displayIssuerName = template.issuerName || data.issuerName || '';
  const displayIssuerTitle = template.issuerTitle || '';

  return (
    <div
      className={cn(
        'relative aspect-[1.414] overflow-hidden rounded-lg shadow-2xl',
        getBackgroundPattern(),
        getBorderStyle(),
        className
      )}
      style={{
        borderColor: template.accentColor,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        ...(template.backgroundColor ? { backgroundColor: template.backgroundColor } : {}),
      }}
    >
      {/* Decorative corners for classic/academic styles */}
      {(template.style === 'classic' || template.style === 'academic') && (
        <>
          <div 
            className="absolute top-4 left-4 w-16 h-16 border-t-4 border-l-4 rounded-tl-lg"
            style={{ borderColor: template.accentColor }}
          />
          <div 
            className="absolute top-4 right-4 w-16 h-16 border-t-4 border-r-4 rounded-tr-lg"
            style={{ borderColor: template.accentColor }}
          />
          <div 
            className="absolute bottom-4 left-4 w-16 h-16 border-b-4 border-l-4 rounded-bl-lg"
            style={{ borderColor: template.accentColor }}
          />
          <div 
            className="absolute bottom-4 right-4 w-16 h-16 border-b-4 border-r-4 rounded-br-lg"
            style={{ borderColor: template.accentColor }}
          />
        </>
      )}

      {/* Modern geometric decoration */}
      {template.style === 'modern' && (
        <div 
          className="absolute top-0 right-0 w-32 h-32 opacity-20"
          style={{
            background: `linear-gradient(135deg, ${template.primaryColor} 0%, transparent 60%)`
          }}
        />
      )}

      {/* Content */}
      <div className={cn('h-full flex flex-col items-center justify-between p-8 text-center', fontClass)}>
        {/* Logo Area */}
        <div className={cn(
          'flex items-center gap-3 w-full',
          template.logoPosition === 'top-center' ? 'justify-center' : 
          template.logoPosition === 'top-left' ? 'justify-start' : 'justify-end'
        )}>
          {template.logoUrl ? (
            <img 
              src={template.logoUrl} 
              alt="Company Logo" 
              className="h-12 w-auto max-w-[120px] object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: template.primaryColor }}
            >
              <Award className="w-6 h-6 text-white" />
            </div>
          )}
          {displayCompanyName && (
            <span className="text-sm font-medium" style={{ color: template.primaryColor }}>
              {displayCompanyName}
            </span>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center space-y-4 py-6">
          {/* Certificate Title */}
          <div className="space-y-1">
            <div className="flex items-center justify-center gap-2">
              <Star className="w-5 h-5" style={{ color: template.accentColor }} />
              <span 
                className="text-xs uppercase tracking-[0.3em] font-medium"
                style={{ color: template.primaryColor }}
              >
                {displayCertTitle}
              </span>
              <Star className="w-5 h-5" style={{ color: template.accentColor }} />
            </div>
            
            {data.certificationName && (
              <h2 
                className="text-2xl font-bold mt-2"
                style={{ color: template.primaryColor }}
              >
                {data.certificationName}
              </h2>
            )}
          </div>

          {/* Recipient Section */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">This is to certify that</p>
            <h1 
              className="text-3xl font-bold tracking-wide"
              style={{ color: template.primaryColor }}
            >
              {data.recipientName}
            </h1>
            <div 
              className="h-0.5 w-48 mx-auto"
              style={{ backgroundColor: template.accentColor }}
            />
          </div>

          {/* Achievement Description */}
          <div className="space-y-2 max-w-md">
            <p className="text-sm text-muted-foreground">
              has successfully completed the learning pathway
            </p>
            <h3 
              className="text-xl font-semibold"
              style={{ color: template.primaryColor }}
            >
              {data.pathwayName}
            </h3>
            {data.score && (
              <p className="text-sm">
                with a score of <span className="font-bold" style={{ color: template.accentColor }}>{data.score}%</span>
              </p>
            )}
          </div>

          {/* Accreditation Text */}
          {template.accreditationText && (
            <p className="text-xs text-muted-foreground max-w-sm italic">
              {template.accreditationText}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="w-full space-y-3">
          <div className="flex items-end justify-between">
            <div className="text-left">
              <p className="text-xs text-muted-foreground">Date Issued</p>
              <p className="text-sm font-medium" style={{ color: template.primaryColor }}>
                {format(data.completionDate, 'MMMM d, yyyy')}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6" style={{ color: template.accentColor }} />
              <span className="text-xs text-muted-foreground">Verified</span>
            </div>
            
            {(displayIssuerName || template.signatureUrl) && (
              <div className="text-right">
                {template.signatureUrl ? (
                  <img 
                    src={template.signatureUrl} 
                    alt="Signature" 
                    className="h-8 w-auto ml-auto object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div 
                    className="h-8 border-b-2"
                    style={{ borderColor: template.primaryColor }}
                  />
                )}
                {displayIssuerName && (
                  <p className="text-xs font-medium mt-1" style={{ color: template.primaryColor }}>
                    {displayIssuerName}
                  </p>
                )}
                {displayIssuerTitle && (
                  <p className="text-xs text-muted-foreground">{displayIssuerTitle}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer Text */}
          {template.footerText && (
            <p className="text-xs text-muted-foreground text-center border-t pt-2">
              {template.footerText}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
